/**
 * PR-STRIPE-EXPRESS-CHECKOUT-ELEMENT-V1
 *
 * Renders two Stripe Elements from the same elements instance:
 *   1. Express Checkout Element (ECE) — Apple Pay / Google Pay buttons
 *      Auto-detects available wallets. Renders nothing if neither is available.
 *   2. Payment Element — card fallback, always rendered below ECE.
 *
 * Both share the same clientSecret-initialized elements object.
 * stripe.confirmPayment() is called by both paths:
 *   - ECE: called inside the 'confirm' event handler (wallet sheet approved)
 *   - Payment Element: called on manual "Pay" button click (card form submit)
 *
 * On success: calls onSuccess() → state machine dispatches STRIPE_CONFIRMED.
 * On payment error: shown inline; card form remains visible for retry.
 * On Stripe.js load failure: shown inline as fatal error.
 *
 * No backend changes: clientSecret and publishableKey come from
 * StripeCheckoutAdapter's stripe_confirm nextAction.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import type {
  Stripe,
  StripeElements,
  StripeExpressCheckoutElement,
  StripePaymentElement,
  StripeExpressCheckoutElementAvailablePaymentMethodsChangeEvent,
} from '@stripe/stripe-js';
import type { CheckoutSession } from '@/lib/types';

interface Props {
  session: CheckoutSession;
  clientSecret: string;
  publishableKey: string;
  returnUrl: string;
  onSuccess: () => void;
}

type LoadState = 'loading' | 'ready' | 'failed';

export function StripeExpressCheckout({ session, clientSecret, publishableKey, returnUrl, onSuccess }: Props) {
  const [loadState, setLoadState] = useState<LoadState>('loading');
  const [hasWallets, setHasWallets] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const stripeRef = useRef<Stripe | null>(null);
  const elementsRef = useRef<StripeElements | null>(null);
  const eceElementRef = useRef<StripeExpressCheckoutElement | null>(null);
  const paymentElementRef = useRef<StripePaymentElement | null>(null);
  const eceContainerRef = useRef<HTMLDivElement>(null);
  const paymentContainerRef = useRef<HTMLDivElement>(null);

  const amountFormatted = new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: session.currency,
    minimumFractionDigits: 2,
  }).format(parseFloat(session.amount));

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const stripe = await loadStripe(publishableKey);
      if (cancelled) return;
      if (!stripe) {
        setLoadState('failed');
        return;
      }

      stripeRef.current = stripe;

      const elements = stripe.elements({
        clientSecret,
        appearance: {
          theme: 'stripe',
          variables: {
            colorPrimary: '#ea580c',
            colorBackground: '#ffffff',
            borderRadius: '12px',
            fontFamily: 'system-ui, -apple-system, sans-serif',
            fontSizeBase: '16px',
          },
        },
      });
      elementsRef.current = elements;

      // --- Express Checkout Element ---
      if (eceContainerRef.current) {
        const eceEl = elements.create('expressCheckout', {
          buttonType: { applePay: 'buy', googlePay: 'buy' },
          buttonTheme: { applePay: 'black', googlePay: 'black' },
          layout: { maxRows: 1, maxColumns: 1 },
        });
        eceElementRef.current = eceEl;

        eceEl.on('availablepaymentmethodschange', (event: StripeExpressCheckoutElementAvailablePaymentMethodsChangeEvent) => {
          if (cancelled) return;
          const methods = event.paymentMethods ?? {};
          setHasWallets(Object.values(methods).some((m) => m?.available));
        });

        eceEl.on('confirm', async () => {
          if (cancelled) return;
          setPaymentError(null);
          const { error } = await stripe.confirmPayment({
            elements,
            confirmParams: { return_url: returnUrl },
            redirect: 'if_required',
          });
          if (cancelled) return;
          if (error) {
            setPaymentError(error.message ?? 'Wallet payment failed. Please try again.');
          } else {
            onSuccess();
          }
        });

        eceEl.mount(eceContainerRef.current);
      }

      // --- Payment Element (card fallback) ---
      if (paymentContainerRef.current) {
        const peEl = elements.create('payment');
        paymentElementRef.current = peEl;
        peEl.mount(paymentContainerRef.current);
      }

      if (!cancelled) setLoadState('ready');
    })();

    return () => {
      cancelled = true;
      try { eceElementRef.current?.destroy(); } catch { /* ignore */ }
      try { paymentElementRef.current?.destroy(); } catch { /* ignore */ }
      eceElementRef.current = null;
      paymentElementRef.current = null;
    };
  }, [clientSecret, publishableKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCardSubmit = useCallback(async () => {
    const stripe = stripeRef.current;
    const elements = elementsRef.current;
    if (!stripe || !elements || isSubmitting) return;

    setIsSubmitting(true);
    setPaymentError(null);

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: returnUrl },
      redirect: 'if_required',
    });

    setIsSubmitting(false);

    if (error) {
      setPaymentError(error.message ?? 'Payment failed. Please try again.');
    } else {
      onSuccess();
    }
  }, [returnUrl, onSuccess, isSubmitting]);

  if (loadState === 'failed') {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center">
          <p className="text-gray-700 mb-4">No se pudo cargar el sistema de pago. Por favor recarga la página.</p>
          <button
            onClick={() => window.location.reload()}
            className="w-full bg-brand-600 text-white font-semibold py-3 px-6 rounded-xl hover:bg-brand-700 transition-colors"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
        <div className="mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-1">Pagar</h2>
          <p className="text-gray-500 text-sm">
            Total: <span className="font-semibold text-gray-900">{amountFormatted}</span>
          </p>
        </div>

        {/* Express Checkout Element — Apple Pay / Google Pay buttons */}
        {/* Renders nothing if no wallets available; separator below is conditional */}
        <div ref={eceContainerRef} data-testid="express-checkout-element" />

        {/* Separator between wallet buttons and card form */}
        {hasWallets && (
          <div className="flex items-center my-4">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="mx-3 text-sm text-gray-400">o pagar con tarjeta</span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>
        )}

        {/* Loading skeleton */}
        {loadState === 'loading' && (
          <div className="flex items-center justify-center py-8">
            <div className="w-6 h-6 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {/* Payment Element — card form fallback */}
        <div ref={paymentContainerRef} data-testid="payment-element" />

        {/* Inline error — shown after wallet or card failure; card form remains visible */}
        {paymentError && (
          <div
            role="alert"
            data-testid="payment-error"
            className="mt-4 p-3 bg-red-50 border border-red-200 rounded-xl"
          >
            <p className="text-sm text-red-700">{paymentError}</p>
          </div>
        )}

        {/* Card submit button — only shown once Stripe.js is ready */}
        {loadState === 'ready' && (
          <button
            data-testid="card-submit-button"
            onClick={handleCardSubmit}
            disabled={isSubmitting}
            className="mt-4 w-full bg-brand-600 text-white font-semibold py-3 px-6 rounded-xl hover:bg-brand-700 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Procesando...' : 'Pagar con tarjeta'}
          </button>
        )}
      </div>
    </div>
  );
}
